// Single cross-package boundary. ACC-502 relocated this file into
// web-front/src/forecast-app/ticketing/graphql/, so the web-front modules it
// needs are reached by native relative paths: ForecastQueryRenderer and
// RelayEnvironment live at web-front/src/ (three levels up); Util lives under
// forecast-app/shared/ (a sibling of ticketing/, two levels up).
// ForecastQueryRenderer is a *named* export in web-front (no default), so import it as such.
export {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
export {graphql} from 'react-relay';
import RelayEnvironment from '../../../RelayEnvironment';
import Util from '../../shared/util/util';
import DirectApi from '../../../directApi';
import {fetchQuery} from 'relay-runtime';
import type {GraphQLTaggedNode} from 'relay-runtime';
import {attachmentProxyPath} from '../features/tickets/utils/attachmentDownloadPath';

export {RelayEnvironment};
export const csrfToken = () => Util.getCsrfValue();
export const socketClientId = () => Util.getClientId();

// ACC-527: open the native Forecast task detail modal (`task_modal_v3`) by
// navigating to `/T{companyTaskId}` on the current path. Exposed through this
// seam (rather than importing Util directly in island code) so the island's
// `Util` touchpoint stays in one place and tests mock it via this module.
export const showNativeTaskModal = (companyTaskId: number, history: unknown) => Util.showTaskModal(companyTaskId, history);

// Resolve a ticketing attachment's backend download URL to an ABSOLUTE,
// same-origin Forecast URL. The backend returns a relative `/api/attachments/...`
// path that would hit the SPA fallback when served from the Forecast origin; we
// rewrite it to the graphql proxy route and prepend the graphql origin the same
// way DirectApi builds fileSrc/companyLogoSrc. See attachmentDownloadPath.ts.
export const ticketingAttachmentDownloadUrl = (downloadUrl: string): string =>
	DirectApi.graphqlServerEndpoint(attachmentProxyPath(downloadUrl));

// This seam forwards to web-front's untyped `Util.CommitMutation` (plain JS, no
// .d.ts). Each ticketing mutation hook passes its own generated module + typed
// `onSuccess`/`onError` lambdas, so type-safety lives at the call sites; here the
// parameters are intentionally `any` to stay a transparent pass-through to the
// untyped wrapper. ACC-450 reconciles this once the source folds into web-front.
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * `onError` receives the wrapper's own error handler as its second argument. Calling it
 * gives the failure Forecast's standard treatment — the not-found / no-viewer modal, the
 * correlation-id error modal, or a retry — and it returns `true` when it has already
 * scheduled that retry, meaning the current attempt is not the final one. Handlers that
 * own the whole failure path (the board reverts optimistically and toasts inline) may
 * ignore it; anything else should forward to it rather than swallow the error.
 */
export function commitTicketingMutation(
	mutationModule: any,
	input: any,
	onSuccess?: (r: any) => void,
	onError?: (e: any, onFailure: (e: any) => boolean | undefined) => void
) {
	return Util.CommitMutation(mutationModule, input, onSuccess, true, null, onError);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function forecastFetchQuery(query: GraphQLTaggedNode, variables: Record<string, unknown>) {
	return fetchQuery(RelayEnvironment.getInstance(), query, variables).toPromise();
}
